import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import debounce from 'lodash/debounce';
import { isPostAuthNav3MobileOrDesktop } from 'util/isPostAuthNav3Active';
import { useResponsiveContext } from 'components/common/ResponsiveContext';
import UploadPlaceholder from 'cards/components/CardListing/PhotoPreview/Upload/UploadPlaceholder';
import { isPhotoPreviewSupported } from 'cards/components/CardListing/PhotoPreview/constants';
import MultiSample from 'cards/components/common/Samples/MultiSample/MultiSample';
import { getUserContext } from 'selectors/user/userSelectors';
import { getMultiSampleState } from 'cards/selectors/samples';
import { getSearchFilters } from 'cards/selectors/cardCatalogSelector';
import { FilterKey, FilterValue } from '../FilterTypes';

import { ActionContainer } from './FloatingActions.style';

const Upload = dynamic(() => import('cards/components/CardListing/PhotoPreview/Upload'), {
  loading: () => <UploadPlaceholder />,
  ssr: false,
});

const isLoadMoreButtonAboveFold = () => {
  const loadMoreBtn = document.getElementById('plp-load-more-button');
  if (!loadMoreBtn || !window) return false;

  const rect = loadMoreBtn.getBoundingClientRect();
  return rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
};

interface FloatingActionsProps {
  updateFilter: (filterKey: FilterKey, filterValue?: FilterValue) => void;
}

export const FloatingActions: React.FC<FloatingActionsProps> = ({ updateFilter }) => {
  const { isMobile } = useResponsiveContext();
  const [visible, setVisible] = useState(true);
  const searchFilters = useSelector(getSearchFilters);
  const multiSampleState = useSelector(getMultiSampleState);
  const userContext = useSelector(getUserContext);
  const isPostAuthNav3 = isPostAuthNav3MobileOrDesktop(userContext);
  const { lead_card_type: cardType } = searchFilters;
  const isPlpPhotoPreview = isPhotoPreviewSupported(cardType);

  // Update the visibility of the floating actions bar on scroll.
  // Hide it whenever the "Load more" button is above the fold.
  useEffect(() => {
    const updateActionsVisibility = debounce(() => {
      setVisible(!isLoadMoreButtonAboveFold());
    });
    updateActionsVisibility();

    window.addEventListener('scroll', updateActionsVisibility);
    return () => {
      updateActionsVisibility.cancel();
      window.removeEventListener('scroll', updateActionsVisibility);
    };
  }, []);

  const showSampleCustomizeButton = multiSampleState.enabled && isMobile;
  const showPhotoPreviewButton = !multiSampleState.enabled && isPlpPhotoPreview;
  const containerIsEmpty = !showSampleCustomizeButton && !showPhotoPreviewButton;

  if (!visible || containerIsEmpty) return null;

  return (
    <ActionContainer isPostAuthNav3={isPostAuthNav3}>
      {showSampleCustomizeButton && <MultiSample.CustomizeButton btnSize="large" />}
      {/* @ts-expect-error Will update Upload component later */}
      {showPhotoPreviewButton && <Upload updateFilter={updateFilter} />}
    </ActionContainer>
  );
};
